import { setProcedures, configureIguazuRPC } from 'iguazu-rpc';
import { configureIguazuREST } from 'iguazu-rest';
import { createTimeoutFetch } from '@americanexpress/fetch-enhancers';
import { getSelectedProduct, getProductsByTypes, getProductAccountKey } from 'axp-product-ducks';
import { createFaasProcedureConfig } from '@americanexpress/iguazu-procedures/utils/createFaasProcedureConfig';
import {
  getApiSearchUrl, populateApiParams, getTypeAheadDetails, /* typeAheadDetails, */
} from '@americanexpress/one-maps-base/src/utils/MapUtils';
import { DEFAULT_MAP_LOCATION } from '@americanexpress/one-maps-base/src/utils/MapCampaigns';
import { parseTypeAheadData } from '@americanexpress/one-maps-base/lib/reducers/reducer';
import memberProducts from '@americanexpress/iguazu-resources/memberProducts';
import hash from 'object-hash';
import parseTypeAheadOneData from './utils/parseTypeAheadOneData';
import getMerchantsApiUrl from './utils/getMerchantsApiUrl';
import getLocale from './utils/getLocale';
import createMapsOneDataProcedureConfig from './utils/createMapsOneDataProcedureConfig';

/* Resources */
const MEMBER_PRODUCTS_RESOURCE = 'memberProducts';

/* Procedures */
// CREATE BDaaS
const CREATE_ACTION_CAPTURE = 'createActionCapture';
// CREATE OneData
const CREATE_MERCHANT_MAP_OFFERS_ENROLLMENT = 'CreateMerchantMapOffersEnrollment';
const CREATE_MERCHANT_MAP_LOG_IMPRESSION = 'CreateMerchantMapLogImpression';
const CREATE_MERCHANT_EDIT_SUGGESTION = 'CreateMerchantEditSuggestion';
// READ OneData (faasApiURL)
const READ_CUSTOMER_PRODUCTS_V2_FUNCTION = 'readCustomerProductsV2';
const READ_CUSTOMER_PRODUCT_DETAILS_V1_FUNCTION = 'readCustomerProductDetailsV1';
const READ_BANKING_SAVINGS_ACCOUNTS_SUMMARY_V1_FUNCTION = 'readBankingSavingsAccountsSummaryV1';
const READ_MERCHANT_PERSONALIZATION_TYPEAHEAD = 'ReadMerchantPersonalizationTypeahead';
const READ_MERCHANT_MAP_OFFERS = 'ReadMerchantMapOffers';
const READ_MERCHANT_MAP_ENROLLED_OFFERS = 'ReadMerchantMapEnrolledOffers';
const READ_MERCHANT_MAP_IB_CATEGORIES = 'ReadMerchantMapIndustryBonusCategories';
const READ_MERCHANT_MAP_RECOMMENDATION = 'ReadMerchantMapRecommendation';
const READ_MERCHANT_PUBLIC_MAP_RECOMMENDATION = 'ReadMerchantPublicMapRecommendation';

const READ_OFFERS_MAPS = 'ReadMerchantMapsTabOffers';

// READ BDaaS (shopsmallMerchantApiUrl)
const LOAD_OFFERS = 'loadOffers';
const LOAD_OFFERS_MAPS = 'loadOffersMaps';
const LOAD_ENROLLED_OFFERS = 'loadEnrolledOffers';
const LOAD_MERCHANT = 'loadMerchants';
const LOAD_INDUSTRY_BONUS = 'loadIndustryBonus';
const LOAD_KEYWORDS = 'loadKeywords';
// READ JSON (gemURL)
const LOAD_NOTIFICATION = 'loadNotification';
const LOAD_PROGRAM_DETAILS = 'loadProgramDetails';
const LOAD_FILTER_LABELS = 'loadFilterlabels';
const LOAD_INTERSTITIAL = 'loadInterstitial';
// UPDATE BDaaS
const UPDATE_OFFER_ENROLLMENT = 'updateOfferEnrollment';
const UPDATE_MERCHANT = 'updateMerchant';

/* istanbul ignore next */
function buildEntry(cacheResult, args) {
  const { offer } = args;
  // eslint-disable-next-line camelcase
  const { treatment_metadata } = cacheResult.treatments[0].component_treatments[0];
  const foundIndex = treatment_metadata.findIndex(
    (element) => element.treatment_identifier === offer.treatment_identifier);
  treatment_metadata.splice(foundIndex, 1);
  const newCacheResult = cacheResult;
  // eslint-disable-next-line camelcase
  newCacheResult.treatments[0].component_treatments[0].treatment_metadata = treatment_metadata;
  return newCacheResult;
}
/* istanbul ignore next */
const getEnv = (state) => {
  const localeFilename = state.getIn(['config', 'localeFilename']);
  let env = 'E3';
  if (localeFilename === 'integration') {
    env = 'E1';
  } else if (localeFilename === 'qa') {
    env = 'E2';
  }
  return env;
};

/* istanbul ignore next */
export const configureIguazuProcedures = () => {
  configureIguazuRPC({
    getToState: (state) => state.getIn(['modules', 'axp-maps-root', 'procedures']),
  });
  setProcedures({
    [READ_MERCHANT_MAP_RECOMMENDATION]: createMapsOneDataProcedureConfig(
      'ReadMerchantMapRecommendation.v1'
    ),
    [READ_MERCHANT_PUBLIC_MAP_RECOMMENDATION]: createMapsOneDataProcedureConfig(
      'ReadMerchantPublicMapRecommendation.v1'
    ),
    [READ_MERCHANT_PERSONALIZATION_TYPEAHEAD]: createMapsOneDataProcedureConfig(
      'ReadMerchantPersonalizationTypeahead.v1', parseTypeAheadOneData
    ),
    [READ_MERCHANT_MAP_IB_CATEGORIES]: createMapsOneDataProcedureConfig(
      'ReadMerchantMapIndustryBonusCategories.v1'
    ),
    [READ_MERCHANT_MAP_OFFERS]: createMapsOneDataProcedureConfig(
      'ReadMerchantMapOffers.v1'
    ),
    [READ_MERCHANT_MAP_ENROLLED_OFFERS]: createMapsOneDataProcedureConfig(
      'ReadMerchantMapEnrolledOffers.v1'
    ),
    [CREATE_MERCHANT_MAP_OFFERS_ENROLLMENT]: createMapsOneDataProcedureConfig(
      'CreateMerchantMapOffersEnrollment.v1'
    ),
    [CREATE_MERCHANT_EDIT_SUGGESTION]: createMapsOneDataProcedureConfig(
      'CreateMerchantEditSuggestion.v1'
    ),
    [CREATE_MERCHANT_MAP_LOG_IMPRESSION]: createMapsOneDataProcedureConfig(
      'CreateMerchantMapLogImpression.v1'
    ),
    [READ_CUSTOMER_PRODUCTS_V2_FUNCTION]: createFaasProcedureConfig(
      'ReadCustomerProducts.v2'
    ),
    [READ_CUSTOMER_PRODUCT_DETAILS_V1_FUNCTION]: createFaasProcedureConfig(
      'ReadCustomerProductDetails.v1'
    ),
    [READ_BANKING_SAVINGS_ACCOUNTS_SUMMARY_V1_FUNCTION]: {
      ...createFaasProcedureConfig('ReadBankingSavingsAccountsSummary.v1'),
    },
    [LOAD_OFFERS]: {
      // eslint-disable-next-line complexity
      call: ({
        getState, args,
      }) => {
        const state = getState();
        const apiKey = getSelectedProduct(state);
        let isAuthenticated = false;
        if (apiKey) {
          isAuthenticated = true;
        }
        let isOffers = false;

        if (args && args.pathname && args.pathname.includes('/maps/offers')) {
          isOffers = true;
        }
        let API_SEARCH_URI = '';
        if (args && args.query && args.pathname) {
          API_SEARCH_URI = getApiSearchUrl(
            populateApiParams(getState, false, isAuthenticated, args.query),
            args.pathname);
        }
        if (isAuthenticated && isOffers && args.query.nw && args.query.se && args.query.country) {
          return fetch(
            `${getMerchantsApiUrl(state)}/api/servicing/v1/maps/offers?${API_SEARCH_URI}`, {
              method: 'GET',
              credentials: 'include',
              headers: {
                'Content-Type': 'application/json',
                account_tokens: apiKey,
                locale: getLocale(state),
              },
            })
            .then((response) => {
              if (response.ok) {
                return response;
              }
              return response.text().then((text) => {
                throw new Error(text);
              });
            })
            .then((res) => res.json());
        }
        return Promise.resolve([]);
      },
      getResultFromCache: ({
        args,
        cache,
      }) => {
        const hashedArgs = hash(`${args.query.cl}/${args.query.nw}/${args.query.se}/${args.accountKey}`);
        if (!cache.has(hashedArgs) || args.forceFetch) {
          throw new Error('make the call');
        }
        return cache.get(hashedArgs);
      },
      buildUpdatedCache: ({
        cache,
        args,
        result,
        error,
      }) => cache.set(hash(`${args.query.cl}/${args.query.nw}/${args.query.se}/${args.accountKey}`), error || result),
      cacheModifier: ({ cache }) => ({ action = 'delete', args, result }) => {
        const hashedArgs = hash(`${args.query.cl}/${args.query.nw}/${args.query.se}/${args.accountKey}`);
        switch (action) {
        case 'delete':
          return cache.delete(hashedArgs);
        case 'update':
          return cache.set(hashedArgs, buildEntry(cache.get(hashedArgs), args, result));
        default:
          return cache;
        }
      },
    },
    [LOAD_ENROLLED_OFFERS]: {
      // eslint-disable-next-line complexity
      call: ({
        getState, args,
      }) => {
        const apiKey = getSelectedProduct(getState());
        let isAuthenticated = false;
        if (apiKey) {
          isAuthenticated = true;
        }
        let isOffers = false;
        if (args && args.pathname && args.pathname.includes('/maps/offers')) {
          isOffers = true;
        }
        let API_SEARCH_URI = '';
        if (args && args.query && args.pathname) {
          API_SEARCH_URI = getApiSearchUrl(
            populateApiParams(getState, false, isAuthenticated, args.query),
            args.pathname);
        }
        if (isAuthenticated && isOffers && args.query.nw && args.query.se && args.query.country) {
          return fetch(
            `${getMerchantsApiUrl(getState())}/api/servicing/v1/maps/offers?${API_SEARCH_URI}&offer_status=enrolled`, {
              method: 'GET',
              credentials: 'include',
              headers: {
                'Content-Type': 'application/json',
                account_tokens: apiKey,
                locale: getLocale(getState()),
              },
            })
            .then((response) => {
              if (response.ok) {
                return response;
              }
              return response.text().then((text) => {
                throw new Error(text);
              });
            })
            .then((res) => res.json());
        }
        // throw new Error('no call');
        return Promise.resolve({ });
      },
      getResultFromCache: ({
        args,
        cache,
      }) => {
        const hashedArgs = hash(`${args.query.cl}/${args.query.nw}/${args.query.se}/${args.accountKey}`);
        if (!cache.has(hashedArgs) || args.query.enrolledOffers) {
          throw new Error('make the call');
        }
        return cache.get(hashedArgs);
      },
      buildUpdatedCache: ({
        cache,
        args,
        result,
        error,
      }) => cache.set(hash(`${args.query.cl}/${args.query.nw}/${args.query.se}/${args.accountKey}`), error || result),
    },
    [LOAD_MERCHANT]: {
      // eslint-disable-next-line complexity
      call: ({ fetchClient, getState, args }) => {
        const apiKey = getSelectedProduct(getState());
        const path = args.pathname.toLowerCase().split('/');
        const locale = path[1];

        let isAuthenticated = false;
        if (apiKey) {
          isAuthenticated = true;
        }
        let isOnline = false;
        if (args.pathname === '/en-us/maps/online') {
          isOnline = true;
        }
        let isOffers = false;
        if (args && args.pathname && args.pathname.includes('/maps/offers')) {
          isOffers = true;
        }

        let API_SEARCH_URI = '';
        if (args && args.query && args.pathname) {
          API_SEARCH_URI = getApiSearchUrl(
            populateApiParams(getState, isOnline, isAuthenticated, args.query, locale),
            args.pathname);
        }

        const fetchOpts = {
          credentials: 'include',
          method: 'GET',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            ...apiKey && { account_tokens: apiKey },
          },
        };

        if (isOnline) {
          return fetchClient(`${getMerchantsApiUrl(getState())}/api/servicing/v1/maps?${API_SEARCH_URI}&country_code=US&online=true`, fetchOpts)
            .then((response) => {
              if (response.ok) {
                return response;
              }
              return response.text().then((text) => {
                throw new Error(text);
              });
            }).then((res) => res.json());
        } if (isOffers || !((args.query && args.query.cl) || (args.query && args.query.mid))
          || !API_SEARCH_URI.includes('country_code') || !args.query.country) {
          return Promise.resolve([]);
        }

        if (Object.keys(args.query).length === 0 || !API_SEARCH_URI.includes('country_code') || !args.query.country) {
          return Promise.resolve([]);
        }

        return fetchClient(`${getMerchantsApiUrl(getState())}/api/servicing/v1/maps?${API_SEARCH_URI}`, fetchOpts)
          .then((response) => {
            if (response.ok) {
              return response;
            }
            return response.text().then((text) => {
              throw new Error(text);
            });
          })
          .then((res) => res.json());
      },
      getResultFromCache: ({
        args,
        cache,
      }) => {
        // const hashedArgs = hash(args);
        const hashedArgs = hash(`${args.accountKey}/${args.search}/${args.pathname}`);
        if (!cache.has(hashedArgs) || args.forceFetch) {
          throw new Error('make the call');
        }
        return cache.get(hashedArgs);
      },
      buildUpdatedCache: ({
        cache,
        args,
        result,
        error,
      }) => cache.set(hash(`${args.accountKey}/${args.search}/${args.pathname}`), (error && []) || result),
    },
    [LOAD_OFFERS_MAPS]: {
      call: ({
        getState, args,
      }) => {
        if (args.merchantOffer && args.query.country) {
          return fetch(
            `${getMerchantsApiUrl(getState())}/api/servicing/v1/maps/offers?country_code=${args.query.country}&treatment_id=${args.merchantOffer}`, {
              method: 'GET',
              credentials: 'include',
              headers: {
                'Content-Type': 'application/json',
                account_tokens: getSelectedProduct(getState()),
                locale: getLocale(getState()),
              },
            })
            .then((response) => {
              if (response.ok) {
                return response;
              }
              return response.text().then((text) => {
                throw new Error(text);
              });
            })
            .then((res) => res.json());
        }
        return Promise.resolve(false);
      },
      getResultFromCache: ({
        args,
        cache,
      }) => {
        const hashedArgs = args.merchantOffer;
        if (!cache.has(hashedArgs) || args.forceFetch) {
          throw new Error('make the call');
        }
        return cache.get(hashedArgs);
      },
      buildUpdatedCache: ({
        cache,
        args,
        result,
        error,
      }) => cache.set(args.merchantOffer, error || result),
    },
    [READ_OFFERS_MAPS]: createMapsOneDataProcedureConfig(
      'ReadMerchantMapsTabOffers.v1'
    ),
    [LOAD_INDUSTRY_BONUS]: {
      // eslint-disable-next-line complexity
      call: ({
        getState, args,
      }) => {
        let isAuthenticated = false;
        if (getSelectedProduct(getState())) {
          isAuthenticated = true;
        }
        let isOffers = false;
        if (args && args.pathname && args.pathname.includes('/maps/offers')) {
          isOffers = true;
        }
        let isOnline = false;
        if (args && args.pathname && args.pathname.includes('/maps/online')) {
          isOnline = true;
        }
        if (isAuthenticated && !isOffers && !isOnline && (args.pathname === '/en-us/maps')) {
          return fetch(
            `${getMerchantsApiUrl(getState())}/api/servicing/v1/maps/industry_bonuses?country_code=US`, {
              method: 'GET',
              credentials: 'include',
              headers: {
                'Content-Type': 'application/json',
                account_tokens: getSelectedProduct(getState()),
              },
            })
            .then((response) => {
              if (response.ok) {
                return response;
              }
              return response.text().then((text) => {
                throw new Error(text);
              });
            })
            .then((res) => res.json());
        }
        return Promise.resolve(false);
      },
      getResultFromCache: ({
        args,
        cache,
      }) => {
        const hashedArgs = `${args.accountKey}/${args.pathname}`;
        if (!cache.has(hashedArgs)) {
          throw new Error('make the call');
        }
        return cache.get(hashedArgs);
      },
      buildUpdatedCache: ({
        cache,
        args,
        result,
        error,
      }) => cache.set(`${args.accountKey}/${args.pathname}`, error || result),
    },
    [UPDATE_OFFER_ENROLLMENT]: {
      call: ({
        getState, args,
      }) => fetch(
        `${getMerchantsApiUrl(getState())}/api/servicing/v1/maps`, {
          method: 'PUT',
          credentials: 'include',
          body:
            JSON.stringify(
              {
                metadata: {
                  action: 'OFFER_ENROLLMENT',
                },
                merchant: {
                  treatment_identifier: args.offer.treatment_identifier,
                  treatment_source_identifier: args.offer.treatment_source_identifier,
                  locale: getLocale(getState()),
                },
              }),
          headers: {
            'Content-Type': 'application/json',
            account_tokens: getSelectedProduct(getState()),
          },
        })
        .then((response) => {
          if (response.ok) {
            return response;
          }
          return response.text().then((text) => {
            throw new Error(text);
          });
        }),
      // .then(res => res.json()),
      getResultFromCache: ({
        args,
        cache,
      }) => {
        const hashedArgs = args.offer.treatment_identifier;
        if (!cache.has(hashedArgs)) {
          throw new Error('make the call');
        }
        return cache.get(hashedArgs);
      },
      buildUpdatedCache: ({
        cache,
        args,
        result,
        error,
      }) => cache.set(args.offer.treatment_identifier, (error && 'fail') || (result && 'pass')),
      modifyOtherCaches: ({ args, result }) => ({
        // modifyCache is a wrapped form of the other procedure's `cacheModifier` result
        [LOAD_OFFERS]: (modifyCache) => modifyCache({ action: 'update', args, result }),
      }),
    },
    [LOAD_KEYWORDS]: {
      // eslint-disable-next-line complexity
      call: ({ fetchClient, getState, args }) => {
        const locale = getLocale(getState());

        let isOnline = false;
        if (args.pathname === '/en-us/maps/online') {
          isOnline = true;
        }

        let API_SEARCH_URI = '';
        if (args && args.query && args.pathname) {
          API_SEARCH_URI = getApiSearchUrl(
            populateApiParams(getState, isOnline, false, args.query, locale),
            args.pathname);
        }

        const typeAhead = getTypeAheadDetails(getEnv(getState()), isOnline ? 'US' : args.locationDetails.country, API_SEARCH_URI);
        const typeAheadRequest = {
          global_request: {
            client_identifier: 'maps',
            channel_identifier: '37',
            country_code: isOnline ? 'US' : args.locationDetails.country,
            request_timestamp: new Date(),
            request_identifier: `maps${new Date().getTime()}`,
          },
          merchant_request_parameters: {
            request_location: {
              geo_center: {
                latitude: args.locationDetails.lat || DEFAULT_MAP_LOCATION[locale].lat,
                longitude: args.locationDetails.lng || DEFAULT_MAP_LOCATION[locale].lng,
              },

            },
            merchant_request_additional_parameters: {
              max_eligible_count: 5,
            },
            merchant_filters: [
              {
                merchant_identifiers: {
                  merchant_type_identifier: isOnline ? 'online' : 'instore',
                },
                merchant_search_identifier: {
                  search_type_key: 'typeahead',
                  search_value: args.keyword,
                },
              },
            ],
          },
        };
        if (typeAhead.listId) {
          typeAheadRequest
            .merchant_request_parameters
            .merchant_filters[0]
            .merchant_identifiers
            .merchant_list_identifier = typeAhead.listId;
        }
        if (args.locationDetails && args.locationDetails.country
          && (args.locationDetails.country.toLowerCase() === 'mx' || args.locationDetails.country.toLowerCase() === 'sg'
            || args.locationDetails.country.toLowerCase() === 'it' || args.locationDetails.country.toLowerCase() === 'es'
            || args.locationDetails.country.toLowerCase() === 'de' || args.locationDetails.country.toLowerCase() === 'fr')) {
          typeAheadRequest
            .merchant_request_parameters
            .merchant_filters[0]
            .merchant_identifiers
            .small_merchant_indicator = 'ALL';
        }

        if (args.locationDetails && args.locationDetails.country && args.locationDetails.country.toLowerCase() === 'nl') {
          typeAheadRequest
            .merchant_request_parameters
            .merchant_filters[0]
            .merchant_identifiers
            .small_merchant_indicator = 'N';
        }

        if (args.locationDetails && args.locationDetails.country && args.locationDetails.country.toLowerCase() === 'jp') {
          typeAheadRequest
            .merchant_request_parameters
            .merchant_filters[0]
            .merchant_identifiers
            .small_merchant_indicator = 'Y';
        }

        const fetchOpts = {
          credentials: 'omit',
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-AMEX-API-KEY': typeAhead.key,
          },
          body: JSON.stringify(typeAheadRequest),
          payload: args.keyword,

        };

        if (args.locationDetails.country || isOnline) {
          return fetchClient(typeAhead.url, fetchOpts)
            .then((response) => {
              if (response.ok) {
                return response;
              }
              return response.text().then((text) => {
                throw new Error(text);
              });
            })
            .then((res) => res.json());
        }
        return Promise.resolve(false);
      },
      getResultFromCache: ({
        args,
        cache,
      }) => {
        const hashedArgs = args.keyword;
        if (!cache.has(hashedArgs)) {
          throw new Error('make the call');
        }
        return cache.get(hashedArgs);
      },
      buildUpdatedCache: ({
        cache,
        args,
        result,
        error,
      }) => cache.set(args.keyword, error || parseTypeAheadData(result)),
    },
    [UPDATE_MERCHANT]: {
      call: ({
        getState, args,
      }) => {
        const apiKey = getProductAccountKey(getState());
        const headers = {};
        headers['Content-Type'] = 'application/json';
        if (apiKey && apiKey.length > 0) {
          headers.account_identifier = apiKey;
          headers.country_code = args.locationDetails.country;
        }
        return fetch(
          `${getMerchantsApiUrl(getState())}/api/servicing/v1/maps`, {
            method: 'PUT',
            credentials: 'include',
            body: JSON.stringify(args.requestObj),
            headers,
          })
          .then((response) => {
            if (response.ok) {
              return response;
            }
            return response.text().then((text) => {
              throw new Error(text);
            });
          });
        // .then(res => res.json());
      },
      getResultFromCache: ({
        args,
        cache,
      }) => {
        const hashedArgs = args.requestObj.merchant.identifier;
        if (!cache.has(hashedArgs)) {
          throw new Error('make the call');
        }
        throw new Error('make the call');
      },
      buildUpdatedCache: ({
        cache,
        args,
        result,
        error,
      }) => cache.set(args.requestObj.merchant.identifier, (error && 'fail') || (result && 'pass')),
    },
    [CREATE_ACTION_CAPTURE]: {
      call: ({
        getState, args,
      }) => {
        const identifiers = getProductsByTypes(getState());
        const headers = {};
        headers['Content-Type'] = 'application/json';
        if (identifiers && identifiers.length > 0) {
          headers.account_identifier = identifiers.join(',');
          headers.country_code = 'US';
        }
        return fetch(
          `${getMerchantsApiUrl(getState())}/api/servicing/v1/maps/impression`, {
            method: 'POST',
            credentials: 'include',
            body: JSON.stringify(args.impressionRequest),
            headers,
          })
          .then((response) => {
            if (response.ok) {
              return response;
            }
            return response.text().then((text) => {
              throw new Error(text);
            });
          })
          .then((res) => res.json());
      },
      getResultFromCache: ({
        args,
        cache,
      }) => {
        const hashedArgs = hash(args);
        if (!cache.has(hashedArgs)) {
          throw new Error('make the call');
        }
        return cache.get(hashedArgs);
      },
      buildUpdatedCache: ({
        cache,
        args,
        result,
        error,
      }) => cache.set(hash(args), error || result),
    },
    [LOAD_INTERSTITIAL]: {
      call: ({ fetchClient, getState, args }) => fetchClient(
        `${getState().getIn(['config', 'gemUrl'])}/content/dam/one-amex/merchant/${args.locale.toLowerCase()}/maps/interstitial.json`
      ).then((response) => response.json()),
      buildUpdatedCache: ({ cache, args, result }) => cache.set(args.locale, result),
      getResultFromCache: ({ args, cache }) => {
        if (!cache.has(args.locale)) {
          throw new Error('make the call');
        }
        return cache.get(args.locale);
      },
    },
    [LOAD_NOTIFICATION]: {
      call: ({ fetchClient, getState, args }) => fetchClient(
        `${getState().getIn(['config', 'gemUrl'])}/content/dam/one-amex/merchant/${args.locale.toLowerCase()}/maps/notification.json`
      ).then((response) => response.json()),
      buildUpdatedCache: ({ cache, args, result }) => cache.set(args.locale, result),
      getResultFromCache: ({ args, cache }) => {
        if (!cache.has(args.locale)) {
          throw new Error('make the call');
        }
        return cache.get(args.locale);
      },
    },
    [LOAD_FILTER_LABELS]: {
      call: ({ fetchClient, getState, args }) => fetchClient(
        `${getState().getIn(['config', 'gemUrl'])}/content/dam/one-amex/merchant/${args.locale.toLowerCase()}/maps/FilterLabel.json`
      ).then((response) => response.json()),
      buildUpdatedCache: ({ cache, args, result }) => cache.set(args.locale, result),
      getResultFromCache: ({ args, cache }) => {
        if (!cache.has(args.locale)) {
          throw new Error('make the call');
        }
        return cache.get(args.locale);
      },
    },
    [LOAD_PROGRAM_DETAILS]: {
      call: ({ fetchClient, getState, args }) => fetchClient(
        `${getState().getIn(['config', 'gemUrl'])}/content/dam/one-amex/merchant/${args.locale.toLowerCase()}/maps/program_detail.json`
      ).then((response) => response.json()),
      buildUpdatedCache: ({ cache, args, result }) => cache.set(args.locale.toLowerCase(), result),
      getResultFromCache: ({ args, cache }) => {
        if (!cache.has(args.locale.toLowerCase())) {
          throw new Error('make the call');
        }
        return cache.get(args.locale.toLowerCase());
      },
    },
  });
};

export const configureIguazuResources = () => {
  configureIguazuREST({
    getToState:
      (state) => state.getIn(['modules', 'axp-maps-root', 'resources']),
    defaultOpts: {
      credentials: 'include',
    },
    composeFetch: createTimeoutFetch(6e3),
    resources: {
      [MEMBER_PRODUCTS_RESOURCE]: memberProducts,
    },
  });
};
